import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { StyledLink } from 'components/common/StyledLink'
import { TutorialStep } from 'components/common/TutorialStep'
import Icon from 'components/Icon'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import useAuth from 'hooks/use-auth'
import { useTranslation } from 'hooks/use-translation'

function getCloudFormationUrl(templateUrl: string, companyId: string) {
  return `https://console.aws.amazon.com/cloudformation/home#/stacks/create/review?templateURL=${templateUrl}&stackName=gremlin-access&param_GremlinCustomerCompanyID=${companyId}`
}

export const AWSCloudFormationRoleAuthentication: React.FC = () => {
  const { companyId } = useAuth()
  const { trans } = useTranslation()
  const { control, formState } = useFormContext<StatusCheckFormValues>()
  const { awsIAMRoleMetadata, isEdit } =
    useObservabilityToolAuthenticationContext()

  const cloudFormationUrl = useMemo(() => {
    if (awsIAMRoleMetadata?.cfnTemplateLink) {
      return getCloudFormationUrl(awsIAMRoleMetadata.cfnTemplateLink, companyId)
    }

    return 'https://console.aws.amazon.com/cloudformation/home'
  }, [awsIAMRoleMetadata?.cfnTemplateLink, companyId])

  return (
    <Stack spacing={2}>
      <Typography>
        {trans(
          'status_checks.authentication.aws.iam_role.cloud_formation_method.title'
        )}
      </Typography>
      <TutorialStep
        badgeNumber={1}
        title={trans(
          'status_checks.authentication.aws.iam_role.cloud_formation_method.step_1.title'
        )}
      >
        <Box ml={1}>
          <Typography>
            {trans(
              'status_checks.authentication.aws.iam_role.cloud_formation_method.step_1.description'
            )}
          </Typography>
        </Box>
      </TutorialStep>
      <TutorialStep
        badgeNumber={2}
        title={trans(
          'status_checks.authentication.aws.iam_role.cloud_formation_method.step_2.title'
        )}
      >
        <Box ml={1} mt={1}>
          <Stack spacing={1}>
            <Typography>
              {trans(
                'status_checks.authentication.aws.iam_role.cloud_formation_method.step_2.description'
              )}
            </Typography>
            <Button
              component={StyledLink}
              href={cloudFormationUrl}
              rel="noopener noreferrer nofollow"
              startIcon={<Icon icon="awsIcon" />}
              sx={{ width: 'fit-content' }}
              target="_blank"
              variant="contained"
            >
              {trans(
                'status_checks.authentication.aws.iam_role.cloud_formation_method.launch_stack_button.label'
              )}
            </Button>
          </Stack>
        </Box>
      </TutorialStep>
      <TutorialStep
        badgeNumber={3}
        title={trans(
          'status_checks.authentication.aws.iam_role.last_step.title'
        )}
      >
        <Box ml={1} mt={1}>
          <Stack spacing={3}>
            <Typography>
              {trans(
                'status_checks.authentication.aws.iam_role.last_step.description'
              )}
            </Typography>
            <Controller
              control={control}
              name="awsIamRoleArn"
              render={({ field }) => (
                <TextField
                  {...field}
                  autoComplete="off"
                  data-testid="aws-iam-role-arn-input"
                  error={Boolean(formState.errors.awsIamRoleArn)}
                  helperText={
                    isEdit && !formState.errors?.awsIamRoleArn
                      ? trans(
                          'status_checks.authentication.aws.iam_role.arn_input.edit.message'
                        )
                      : formState.errors?.awsIamRoleArn?.message
                  }
                  label={trans(
                    'status_checks.authentication.aws_iam_role_arn.text_field.label'
                  )}
                  variant="outlined"
                />
              )}
            />
          </Stack>
        </Box>
      </TutorialStep>
    </Stack>
  )
}
